/**
 * LanguageSwitcher
 *
 * Header control for choosing the front-end language. Renders nothing when
 * only one locale is available — a picker with a single option is noise, and
 * that's the normal state for a retailer portal that offers one language.
 *
 * Two variants because the header has two menus: a Bootstrap dropdown on
 * desktop (matching the Menu / User icon buttons) and flat nav items inside
 * the collapsed mobile menu.
 */

import React from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'desktop' }) => {
  const { locale, locales, setLocale } = useLanguage();
  const { t } = useTranslation('common');

  if (locales.length < 2) return null;

  const label = t('language.switcher', 'Language');
  const active = locales.find(l => l.code === locale);

  if (variant === 'mobile') {
    return (
      <>
        <li><hr className="dropdown-divider" /></li>
        <li>
          <span className="dropdown-item-text small text-muted">
            <Globe size={14} className="me-1" /> {label}
          </span>
        </li>
        {locales.map(l => (
          <li key={l.code} className="nav-item">
            <button
              type="button"
              className="nav-link btn btn-link text-start w-100"
              lang={l.code}
              aria-current={l.code === locale ? 'true' : undefined}
              onClick={() => setLocale(l.code)}
            >
              {l.code === locale ? <Check size={16} className="me-1" /> : <span className="me-3" />}
              {l.nativeName}
            </button>
          </li>
        ))}
      </>
    );
  }

  return (
    <div className="dropdown d-none d-md-block">
      <button
        type="button"
        className="btn btn-outline-light btn-sm header-icon-btn d-flex"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        // The accessible name carries the current language so a screen-reader
        // user knows what's selected without opening the menu.
        title={`${label}: ${active?.nativeName ?? locale}`}
        aria-label={`${label}: ${active?.nativeName ?? locale}`}
      >
        <Globe size={18} />
      </button>
      <ul className="dropdown-menu dropdown-menu-end">
        <li><span className="dropdown-item-text small text-muted">{label}</span></li>
        <li><hr className="dropdown-divider" /></li>
        {locales.map(l => (
          <li key={l.code}>
            <button
              type="button"
              className={`dropdown-item${l.code === locale ? ' active' : ''}`}
              // Tag each option in its own language so screen readers pronounce
              // "Français" in French rather than reading it as English.
              lang={l.code}
              aria-current={l.code === locale ? 'true' : undefined}
              onClick={() => setLocale(l.code)}
            >
              {l.code === locale ? <Check size={16} className="me-1" /> : <span className="me-3" />}
              {l.nativeName}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageSwitcher;
